import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone, ArrowDown } from "lucide-react";
import { MaskLine } from "./Reveal";
import { anchorScroll, ctaGo } from "./scroll";

const HERO_IMG =
  "https://images.unsplash.com/photo-1748063578185-3d68121b11ff?q=80&auto=format&fit=crop&w=2400";

const HERO_STATS = [
  "Australian Credit Licence 389328",
  "Credit Representative 554728",
  "PO Box 4012, Lugarno NSW 2210",
];

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "16%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.04, 1.14]);
  const fade = useTransform(scrollYProgress, [0, 0.75], [1, 0.25]);

  return (
    <section
      id="top"
      ref={ref}
      className="relative flex min-h-[100svh] flex-col overflow-hidden bg-ivory"
    >
      <motion.div style={{ y, scale }} className="absolute inset-0">
        <img
          src={HERO_IMG}
          alt="Modern Australian home glowing with warm light at dusk"
          fetchPriority="high"
          className="h-full w-full object-cover saturate-[0.82] brightness-[1.06]"
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-ivory via-ivory/80 to-ivory/15" />
      <div className="absolute inset-0 bg-gradient-to-t from-ivory via-transparent to-ivory/50" />

      <motion.div
        style={{ opacity: fade }}
        className="relative mx-auto flex w-full max-w-[88rem] flex-1 items-center px-6 pt-32 pb-16 lg:px-12"
      >
        <div className="max-w-5xl">
          <MaskLine delay={0.35}>
            <span className="mb-7 inline-flex items-center gap-4 font-mono text-[11px] uppercase tracking-[0.3em] text-eucalyptus sm:text-xs">
              <span className="h-px w-10 bg-eucalyptus/70" />
              Sydney Mortgage Broker — Toro Finance
            </span>
          </MaskLine>

          <h1 className="font-display text-[clamp(2.7rem,6.2vw,5.8rem)] font-medium leading-[1.06] tracking-[-0.01em] text-ink">
            <MaskLine delay={0.5}>Is your home loan</MaskLine>
            <MaskLine delay={0.65}>
              <span className="italic text-eucalyptus">
                still working for you?
              </span>
            </MaskLine>
          </h1>

          <MaskLine delay={0.9}>
            <p className="mt-8 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg">
              Sydney mortgage broker. Get a free annual loan review and a plan
              to pay your home loan off sooner.
            </p>
          </MaskLine>

          <MaskLine delay={1.05}>
            <div className="mt-10 flex flex-col gap-4 sm:flex-row sm:items-center">
              <a
                href="tel:0414345736"
                onClick={ctaGo}
                data-testid="hero-cta-call"
                className="group inline-flex items-center justify-center gap-3 rounded-full bg-ink px-8 py-4 text-sm font-semibold text-ivory transition-all duration-300 hover:-translate-y-0.5 hover:bg-eucalyptus hover:text-ivory"
              >
                <Phone className="h-4 w-4 transition-transform duration-300 group-hover:rotate-12" />
                See how my loan stacks up
              </a>
              <a
                href="#services"
                data-testid="hero-cta-services"
                onClick={(e) => anchorScroll(e, "services")}
                className="group inline-flex items-center justify-center gap-3 rounded-full border border-ink/25 px-8 py-4 text-sm font-medium text-ink transition-all duration-300 hover:border-eucalyptus hover:text-eucalyptus"
              >
                Explore what we do
                <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-1" />
              </a>
            </div>
          </MaskLine>
        </div>
      </motion.div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 1, delay: 1.4 }}
        className="relative border-t border-ink/10 bg-ivory/70 backdrop-blur-sm"
        data-testid="hero-trust-bar"
      >
        <div className="mx-auto grid max-w-[88rem] grid-cols-1 gap-2 px-6 py-5 sm:grid-cols-3 sm:gap-4 lg:px-12">
          {HERO_STATS.map((s, i) => (
            <p
              key={s}
              className={`font-mono text-[10px] uppercase tracking-[0.22em] text-ink/55 sm:text-[11px] ${
                i > 0 ? "sm:border-l sm:border-ink/10 sm:pl-6" : ""
              }`}
            >
              {s}
            </p>
          ))}
        </div>
      </motion.div>
    </section>
  );
};

export default Hero;
